import { Fragment } from 'react';

import { Typography } from '@material-ui/core';

import { extractLastNameComponent } from '@spotify/confidence-core-react';

import type { PreviewField } from '../../extensions';
import { useResolveResourceName } from '../ToolConfirmationMessage/ResourceDisplayNameContext';
import {
  getNestedValue,
  hasNestedKey,
  humanizeKey,
} from '../ToolConfirmationMessage/inputUtils';
import styles from './ConfirmationConfigGrid.module.css';

const resolveStringValue = (
  value: string,
  resolve: (name: string) => string,
): string => {
  const resolved = resolve(value);
  if (resolved !== value) return resolved;
  return extractLastNameComponent(value) ?? value;
};

const ConfigValue = ({ value }: { value: unknown }) => {
  const resolve = useResolveResourceName();

  if (value === null || value === undefined) return null;

  if (typeof value === 'string') {
    return (
      <Typography variant="caption">
        {resolveStringValue(value, resolve)}
      </Typography>
    );
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return <Typography variant="caption">{String(value)}</Typography>;
  }

  if (Array.isArray(value)) {
    const display = value
      .map(v => {
        if (typeof v === 'string') return resolveStringValue(v, resolve);
        if (typeof v === 'object' && v !== null) {
          const obj = v as Record<string, unknown>;
          const label = obj.displayName ?? obj.name ?? obj.variant;
          if (label !== undefined) {
            const raw = String(label);
            return extractLastNameComponent(raw) ?? raw;
          }
          return JSON.stringify(v);
        }
        return String(v);
      })
      .join(', ');
    return <Typography variant="caption">{display}</Typography>;
  }

  return <Typography variant="caption">{JSON.stringify(value)}</Typography>;
};

interface ConfirmationConfigGridProps {
  input: Record<string, unknown>;
  previewFields: PreviewField[];
}

export const ConfirmationConfigGrid = ({
  input,
  previewFields,
}: ConfirmationConfigGridProps) => {
  const visibleFields = previewFields.filter(f => hasNestedKey(input, f.key));

  if (visibleFields.length === 0) return null;

  return (
    <div className={styles.configGrid}>
      {visibleFields.map(field => (
        <Fragment key={field.key}>
          <Typography variant="caption" color="textSecondary">
            {field.label ?? humanizeKey(field.key.split('.').pop()!)}
          </Typography>
          <ConfigValue value={getNestedValue(input, field.key)} />
        </Fragment>
      ))}
    </div>
  );
};
